import Grid from '@precis-digital/kurama/Grid'
import { useTheme } from '@precis-digital/kurama/utils'
import type { ChartDataRow } from '../../../types'
import { isValidElement, type ReactElement } from 'react'
import { StyledBodyText } from '../styled'
import {
  extractDoubleCurlyBracketPlaceholders,
  getMetricsOrDimensionsMap,
  replaceDoubleCurlyBracketPlaceholders
} from '../utils'
import type { ChartCardTableBodyCellContextResolved } from '../tanstack'
import { useChartCardDefaults } from '../../../hooks/useChartCardDefaults'

interface TableBodyDoubleLineCellProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  firstLine: string
  secondLine: string
  context: ChartCardTableBodyCellContextResolved<DataKey, DataRow>
}

const TableBodyDoubleLineCell = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  firstLine: firstLineText,
  secondLine: secondLineText,
  context
}: TableBodyDoubleLineCellProps<DataKey, DataRow>): ReactElement => {
  const defaults = useChartCardDefaults()
  const theme = useTheme()
  const firstLineDataKeys = extractDoubleCurlyBracketPlaceholders<DataKey>(firstLineText)
  const firstLineMap = getMetricsOrDimensionsMap<DataKey, DataRow>(context, firstLineDataKeys, 'h4', theme, defaults)
  const firstLine = replaceDoubleCurlyBracketPlaceholders(theme, firstLineText, firstLineMap)

  const firstLineElement = isValidElement(firstLine) ? (
    firstLine
  ) : (
    <StyledBodyText variant="h4" textAlign="left">
      {firstLine}
    </StyledBodyText>
  )

  const secondLineDataKeys = extractDoubleCurlyBracketPlaceholders<DataKey>(secondLineText)
  const secondLineMap = getMetricsOrDimensionsMap<DataKey, DataRow>(
    context,
    secondLineDataKeys,
    'body2',
    theme,
    defaults
  )
  const secondLine = replaceDoubleCurlyBracketPlaceholders(theme, secondLineText, secondLineMap)

  const secondLineElement = isValidElement(secondLine) ? (
    secondLine
  ) : (
    <StyledBodyText variant="body2" textAlign="left">
      {secondLine}
    </StyledBodyText>
  )

  return (
    <Grid container direction="column" alignItems="start" justifyContent="center" width="100%">
      {firstLineElement}
      {secondLineElement}
    </Grid>
  )
}

export default TableBodyDoubleLineCell
